import { PLACEHOLDERS, PlaceholderItem } from "./placeholders";

/* global Office, Word, navigator */

const FAVORITES_STORAGE_KEY = "dokplatzhalter_favorites";
const RECENT_STORAGE_KEY = "dokplatzhalter_recent";
const COLLAPSED_STORAGE_KEY = "dokplatzhalter_collapsed";
const MAX_RECENTS = 12;

type PlaceholderGroup = {
  [category: string]: {
    [subcategory: string]: PlaceholderItem[];
  };
};

Office.onReady(() => {
  initializeApp();
});

function initializeApp(): void {
  const searchInput = document.getElementById("searchInput") as HTMLInputElement | null;
  const clearRecentBtn = document.getElementById("clearRecentBtn") as HTMLButtonElement | null;

  searchInput?.addEventListener("input", () => renderAll(searchInput.value.trim()));

  clearRecentBtn?.addEventListener("click", () => {
    saveRecents([]);
    renderAll(searchInput?.value.trim() ?? "");
  });

  // Initialize all categories as collapsed if no collapsed state is saved
  const collapsed = getCollapsedSections();
  if (collapsed.length === 0) {
    const grouped = groupPlaceholders(PLACEHOLDERS);
    const allCollapsed: string[] = [];
    Object.keys(grouped).forEach(category => {
      allCollapsed.push(`cat:${category}`);
      Object.keys(grouped[category]).forEach(subcategory => {
        allCollapsed.push(`cat:${category}|sub:${subcategory}`);
      });
    });
    saveCollapsedSections(allCollapsed);
  }

  renderAll("");
}

function renderAll(searchTerm: string): void {
  renderFavorites(searchTerm);
  renderRecents(searchTerm);
  renderCategories(searchTerm);
}

function getFavorites(): string[] {
  try {
    return JSON.parse(localStorage.getItem(FAVORITES_STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveFavorites(keys: string[]): void {
  localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(keys));
}

function getRecents(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveRecents(keys: string[]): void {
  localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(keys));
}

function addRecent(key: string): void {
  const current = getRecents().filter((x) => x !== key);
  current.unshift(key);
  saveRecents(current.slice(0, MAX_RECENTS));
}

function getCollapsedSections(): string[] {
  try {
    return JSON.parse(localStorage.getItem(COLLAPSED_STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveCollapsedSections(keys: string[]): void {
  localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify(keys));
}

function isCollapsed(sectionKey: string): boolean {
  return getCollapsedSections().includes(sectionKey);
}

function toggleCollapsed(sectionKey: string): void {
  const collapsed = getCollapsedSections();
  if (collapsed.includes(sectionKey)) {
    saveCollapsedSections(collapsed.filter((x) => x !== sectionKey));
  } else {
    collapsed.push(sectionKey);
    saveCollapsedSections(collapsed);
  }

  const searchInput = document.getElementById("searchInput") as HTMLInputElement | null;
  renderAll(searchInput?.value.trim() ?? "");
}

function isFavorite(item: PlaceholderItem): boolean {
  const dynamicFavorites = getFavorites();
  return item.favorite === true || dynamicFavorites.includes(item.key);
}

function toggleFavorite(key: string): void {
  const favorites = getFavorites();
  if (favorites.includes(key)) {
    saveFavorites(favorites.filter((x) => x !== key));
  } else {
    favorites.push(key);
    saveFavorites(favorites);
  }

  const searchInput = document.getElementById("searchInput") as HTMLInputElement | null;
  renderAll(searchInput?.value.trim() ?? "");
}

function filterItems(items: PlaceholderItem[], searchTerm: string): PlaceholderItem[] {
  const q = searchTerm.toLowerCase();

  if (!q) {
    return items;
  }

  return items.filter((item) => {
    return (
      item.label.toLowerCase().includes(q) ||
      item.key.toLowerCase().includes(q) ||
      item.category.toLowerCase().includes(q) ||
      (item.subcategory || "").toLowerCase().includes(q) ||
      (item.description || "").toLowerCase().includes(q)
    );
  });
}

function renderFavorites(searchTerm: string): void {
  const container = document.getElementById("favoritesContainer");
  if (!container) return;

  const items = filterItems(PLACEHOLDERS.filter((x) => isFavorite(x)), searchTerm);
  renderFlatList(container, items, "Keine Favoriten vorhanden.");
}

function renderRecents(searchTerm: string): void {
  const container = document.getElementById("recentContainer");
  if (!container) return;

  const recentKeys = getRecents();
  const items = recentKeys
    .map((key) => PLACEHOLDERS.find((x) => x.key === key))
    .filter((x): x is PlaceholderItem => Boolean(x));

  const filtered = filterItems(items, searchTerm);
  renderFlatList(container, filtered, "Noch keine zuletzt verwendeten Platzhalter.");
}

function renderFlatList(container: HTMLElement, items: PlaceholderItem[], emptyText: string): void {
  container.innerHTML = "";

  if (items.length === 0) {
    const empty = document.createElement("div");
    empty.className = "empty-state";
    empty.textContent = emptyText;
    container.appendChild(empty);
    return;
  }

  items.forEach((item) => {
    container.appendChild(createPlaceholderCard(item));
  });
}

function renderCategories(searchTerm: string): void {
  const container = document.getElementById("categoriesContainer");
  if (!container) return;

  container.innerHTML = "";

  const filtered = filterItems(PLACEHOLDERS, searchTerm);
  const grouped = groupPlaceholders(filtered);

  const categories = Object.keys(grouped).sort((a, b) => a.localeCompare(b, "de"));

  if (categories.length === 0) {
    const empty = document.createElement("div");
    empty.className = "empty-state";
    empty.textContent = "Keine Platzhalter gefunden.";
    container.appendChild(empty);
    return;
  }

  categories.forEach((category) => {
    const categoryKey = `cat:${category}`;
    const categorySection = document.createElement("div");
    categorySection.className = "category-section";

    const categoryHeader = document.createElement("button");
    categoryHeader.type = "button";
    categoryHeader.className = "section-toggle";
    categoryHeader.innerHTML = `
      <span>${escapeHtml(category)}</span>
      <span class="section-toggle-icon">${isCollapsed(categoryKey) ? "▸" : "▾"}</span>
    `;
    categoryHeader.addEventListener("click", () => toggleCollapsed(categoryKey));
    categorySection.appendChild(categoryHeader);

    if (!isCollapsed(categoryKey)) {
      const subcategories = Object.keys(grouped[category]).sort((a, b) => a.localeCompare(b, "de"));
      const body = document.createElement("div");
      body.className = "section-body";

      subcategories.forEach((subcategory) => {
        const subcategoryKey = `cat:${category}|sub:${subcategory}`;
        const subSection = document.createElement("div");
        subSection.className = "subcategory-section";

        const subHeader = document.createElement("button");
        subHeader.type = "button";
        subHeader.className = "subsection-toggle";
        subHeader.innerHTML = `
          <span>${escapeHtml(subcategory)}</span>
          <span class="section-toggle-icon">${isCollapsed(subcategoryKey) ? "▸" : "▾"}</span>
        `;
        subHeader.addEventListener("click", () => toggleCollapsed(subcategoryKey));
        subSection.appendChild(subHeader);

        if (!isCollapsed(subcategoryKey)) {
          const subBody = document.createElement("div");
          subBody.className = "subsection-body";

          grouped[category][subcategory].forEach((item) => {
            subBody.appendChild(createPlaceholderCard(item));
          });

          subSection.appendChild(subBody);
        }

        body.appendChild(subSection);
      });

      categorySection.appendChild(body);
    }

    container.appendChild(categorySection);
  });
}

function groupPlaceholders(items: PlaceholderItem[]): PlaceholderGroup {
  const grouped: PlaceholderGroup = {};

  items.forEach((item) => {
    const category = item.category || "Sonstige";
    const subcategory = item.subcategory || "Allgemein";

    if (!grouped[category]) {
      grouped[category] = {};
    }

    if (!grouped[category][subcategory]) {
      grouped[category][subcategory] = [];
    }

    grouped[category][subcategory].push(item);
  });

  Object.keys(grouped).forEach((category) => {
    Object.keys(grouped[category]).forEach((subcategory) => {
      grouped[category][subcategory].sort((a, b) => a.label.localeCompare(b.label, "de"));
    });
  });

  return grouped;
}

function createPlaceholderCard(item: PlaceholderItem): HTMLElement {
  const row = document.createElement("div");
  row.className = "placeholder-row";

  const insertBtn = document.createElement("button");
  insertBtn.type = "button";
  insertBtn.className = "placeholder-main-button";
  insertBtn.textContent = item.label;
  insertBtn.title = `{${item.key}} einfügen`;
  insertBtn.addEventListener("click", async () => {
    await insertPlaceholder(item.key);
    addRecent(item.key);
    renderAll((document.getElementById("searchInput") as HTMLInputElement | null)?.value.trim() ?? "");
    showTemporaryButtonFeedback(insertBtn, "Eingefügt");
  });

  const favoriteBtn = document.createElement("button");
  favoriteBtn.type = "button";
  favoriteBtn.className = "placeholder-side-button";
  favoriteBtn.title = isFavorite(item) ? "Aus Favoriten entfernen" : "Zu Favoriten hinzufügen";
  favoriteBtn.textContent = isFavorite(item) ? "★" : "☆";
  favoriteBtn.addEventListener("click", (event) => {
    event.stopPropagation();
    toggleFavorite(item.key);
  });

  const copyBtn = document.createElement("button");
  copyBtn.type = "button";
  copyBtn.className = "placeholder-side-button";
  copyBtn.title = `{${item.key}} kopieren`;
  copyBtn.textContent = "⧉";
  copyBtn.addEventListener("click", async (event) => {
    event.stopPropagation();
    await copyToClipboard(`{${item.key}}`);
    showTemporaryButtonFeedback(copyBtn, "✓");
  });

  row.appendChild(insertBtn);
  row.appendChild(favoriteBtn);
  row.appendChild(copyBtn);

  return row;
}

async function insertPlaceholder(key: string): Promise<void> {
  try {
    await Word.run(async (context) => {
      const selection = context.document.getSelection();
      const insertedRange = selection.insertText(`{${key}}`, Word.InsertLocation.replace);

      const endRange = insertedRange.getRange("End");
      endRange.select();

      await context.sync();
    });
  } catch (error) {
    console.error("Fehler beim Einfügen:", error);
  }
}

async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {
    console.error("Fehler beim Kopieren:", error);
  }
}

function showTemporaryButtonFeedback(button: HTMLButtonElement, tempText: string): void {
  const original = button.textContent || "";
  button.textContent = tempText;
  button.disabled = true;

  window.setTimeout(() => {
    button.textContent = original;
    button.disabled = false;
  }, 900);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}